import React, {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  useCallback,
} from "react";
import { useAuth } from "./AuthContext";
import { genericService } from "../services/generic.service";
import { usersService } from "../services/users.service";
import { customFieldsService } from "../services/customFields.service";
import { personsService } from "../services/persons.service";
import { companiesService } from "../services/companies.service";
import { leadsService } from "../services/leads.service";
import { mailsService } from "../services/mails.service";

const LeadContext = createContext(null);

export const LeadProvider = ({ children }) => {
  const { auth, accounts, supervisedAccounts, loading} = useAuth();

  const [statuses, setStatuses] = useState([]);
  const [usuarios, setUsuarios] = useState([]);
  const [types, setTypes] = useState([]);
  const [currentUser, setCurrentUser] = useState(null);
  const [countries, setCountries] = useState([]);
  const [responsible, setResponsible] = useState([]);

  const [customFields, setCustomFields] = useState([]);
  const [customFieldsLoading, setCustomFieldsLoading] = useState(false);
  const [customFieldsError, setCustomFieldsError] = useState(null);

  const [leadCustomFieldValues, setLeadCustomFieldValues] = useState({});
  const [leadCustomFieldValuesLoading, setLeadCustomFieldValuesLoading] = useState(false);
  const [leadCustomFieldValuesError, setLeadCustomFieldValuesError] = useState(null);

  const [companies, setCompanies] = useState([]);
  const [companiesLoading, setCompaniesLoading] = useState(false);
  const [companiesError, setCompaniesError] = useState(null);

  const [companyDetail, setCompanyDetail] = useState(null);
  const [companyDetailLoading, setCompanyDetailLoading] = useState(false);
  const [companyDetailError, setCompanyDetailError] = useState(null);

  const [personsByCompany, setPersonsByCompany] = useState([]);
  const [personsByCompanyLoading, setPersonsByCompanyLoading] = useState(false);
  const [personsByCompanyError, setPersonsByCompanyError] = useState(null);

  const [communicationSubjects, setCommunicationSubjects] = useState([]);
  const [communicationSubjectsLoading, setCommunicationSubjectsLoading] = useState(false);
  const [communicationSubjectsError, setCommunicationSubjectsError] = useState(null);
  const [leadUnreadStatus, setLeadUnreadStatus] = useState(null);
  const communicationSubjectsRef = useRef([]);
  const communicationSubjectsLeadRef = useRef(null);
  const subjectsEventSequenceRef = useRef(0);
  const latestSubjectsFetchRef = useRef(0);
  const latestConversationFetchRef = useRef(0);
  const completedReadSequencesRef = useRef(new Map());

  useEffect(() => {
    communicationSubjectsRef.current = communicationSubjects;
  }, [communicationSubjects]);

  const [selectedCommunicationSubjectId, setSelectedCommunicationSubjectId] = useState(null);

  const [selectedCommunicationSubject, setSelectedCommunicationSubject] = useState(null);

  const [conversationMessages, setConversationMessages] = useState([]);
  const [conversationParticipants, setConversationParticipants] = useState([]);
  const [conversationLoading, setConversationLoading] = useState(false);
  const [conversationError, setConversationError] = useState(null);
  const [conversationReminders, setConversationReminders] = useState([]);
  const [leadReminderStatus, setLeadReminderStatus] = useState(null);

  const applyConversationReminders = useCallback((data, leadId) => {
    if (!data || typeof data !== "object") return;

    if (Array.isArray(data.reminders)) {
      setConversationReminders(data.reminders);
    }

    if (leadId != null && typeof data.lead_has_due_reminder === "boolean") {
      setLeadReminderStatus({
        leadId: Number(leadId),
        hasDueReminder: data.lead_has_due_reminder,
      });
    }
  }, []);


  const isAuthenticated = !!auth;
  const currentUsername = auth?.user?.username;

  const fetchCommunicationSubjects = useCallback(async (params = {}) => {
    const fetchLeadId = params.lead_id != null ? Number(params.lead_id) : null;
    const fetchSequence = ++subjectsEventSequenceRef.current;
    latestSubjectsFetchRef.current = fetchSequence;
    communicationSubjectsLeadRef.current = fetchLeadId;
    setCommunicationSubjectsLoading(true);
    setCommunicationSubjectsError(null);
    const isLatestFetch = () => latestSubjectsFetchRef.current === fetchSequence;

    try {
      const data = await mailsService.fetchCommunicationSubjects(params);
      if (!isLatestFetch()) return data;
      const fetchedSubjects = Array.isArray(data) ? data : data?.results || [];
      const subjects = fetchedSubjects.map((subject) => {
        const readSequence = completedReadSequencesRef.current.get(
          `${fetchLeadId}:${Number(subject?.subject_group_id ?? subject?.id)}`,
        );
        return readSequence > fetchSequence
          ? { ...subject, has_unread: false }
          : subject;
      });
      communicationSubjectsRef.current = subjects;
      setCommunicationSubjects(subjects);
      return data;
    } catch (error) {
      console.error("Error fetching communication subjects:", error?.response?.data || error);
      if (!isLatestFetch()) return [];
      communicationSubjectsRef.current = [];
      setCommunicationSubjects([]);
      setCommunicationSubjectsError(error);
      return [];
    } finally {
      if (isLatestFetch()) setCommunicationSubjectsLoading(false);
    }
  }, []);

  const fetchConversationMessages = useCallback(async (params = {}) => {
    const fetchSequence = ++latestConversationFetchRef.current;
    const isLatestFetch = () =>
      latestConversationFetchRef.current === fetchSequence;
    const isForActiveLead = () =>
      params.lead_id == null ||
      communicationSubjectsLeadRef.current == null ||
      Number(communicationSubjectsLeadRef.current) === Number(params.lead_id);
    setConversationLoading(true);
    setConversationError(null);

    try {
      const data = await mailsService.fetchLeadConversation(params);
      const results = Array.isArray(data) ? data : data?.results || [];
      const participants = Array.isArray(data?.participants) ? data.participants : [];

      if (isLatestFetch() && isForActiveLead()) {
        setConversationMessages(results);
        setConversationParticipants(participants);
        applyConversationReminders(
          {
            reminders: Array.isArray(data?.reminders) ? data.reminders : [],
            lead_has_due_reminder: data?.lead_has_due_reminder,
          },
          params.lead_id,
        );
      }

      if (params.lead_id && params.subject_group_id) {
        const readLeadId = Number(params.lead_id);
        const subjectsAtRead = communicationSubjectsRef.current || [];

        mailsService
          .markConversationRead({
            leadId: params.lead_id,
            subjectGroupId: params.subject_group_id,
          })
          .then(() => {
            completedReadSequencesRef.current.set(
              `${readLeadId}:${Number(params.subject_group_id)}`,
              ++subjectsEventSequenceRef.current,
            );
            const stillOnTheLead =
              communicationSubjectsLeadRef.current === readLeadId;
            const currentSubjects = stillOnTheLead
              ? communicationSubjectsRef.current || []
              : subjectsAtRead;

            const markSubjectRead = (subjects) =>
              subjects.map((subject) =>
                Number(subject?.subject_group_id ?? subject?.id) ===
                Number(params.subject_group_id)
                  ? { ...subject, has_unread: false }
                  : subject,
              );
            const readSubjects = markSubjectRead(currentSubjects);

            if (stillOnTheLead) {
              communicationSubjectsRef.current = readSubjects;
              setCommunicationSubjects((latestSubjects) => {
                const latestReadSubjects = markSubjectRead(latestSubjects || []);
                communicationSubjectsRef.current = latestReadSubjects;
                return latestReadSubjects;
              });
            }

            setLeadUnreadStatus({
              leadId: readLeadId,
              hasUnread: readSubjects.some((subject) => subject?.has_unread),
            });
          })
          .catch((markReadError) => {
            console.error(
              "Error marking conversation as read:",
              markReadError?.response?.data || markReadError,
            );
          });
      }

      return data;
    } catch (error) {
      console.error(
        "Error fetching conversation messages:",
        error?.response?.data || error,
      );
      if (!isLatestFetch() || !isForActiveLead()) return null;
      setConversationMessages([]);
      setConversationParticipants([]);
      setConversationReminders([]);
      setConversationError(error);
      return null;
    } finally {
      if (isLatestFetch()) setConversationLoading(false);
    }
  }, [applyConversationReminders]);

  const fetchCompanyDetail = useCallback(async (companyId) => {
    if (!companyId) {
      setCompanyDetail(null);
      return null;
    }

    setCompanyDetailLoading(true);
    setCompanyDetailError(null);

    try {
      const data = await companiesService.getDetail(companyId);
      setCompanyDetail(data);
      return data;
    } catch (error) {
      console.error("Error fetching company detail:", error?.response?.data || error);
      setCompanyDetail(null);
      setCompanyDetailError(error);
      return null;
    } finally {
      setCompanyDetailLoading(false);
    }
  }, []);


  const fetchPersonsByCompany = useCallback(async (companyId) => {
    setPersonsByCompanyLoading(true);
    setPersonsByCompanyError(null);

    try {
      const data = await personsService.getPersons(
        companyId ? { companyId } : {},
      );

      const list = Array.isArray(data) ? data : [];
      setPersonsByCompany(list);
      return list;
    } catch (error) {
      console.error("Error fetching persons:", error?.response?.data || error);
      setPersonsByCompany([]);
      setPersonsByCompanyError(error);
      return [];
    } finally {
      setPersonsByCompanyLoading(false);
    }
  }, []);

  const fetchLeadCustomFieldValues = useCallback(async (leadId) => {
    if (!leadId) {
      setLeadCustomFieldValues({});
      return;
    }

    setLeadCustomFieldValuesLoading(true);
    setLeadCustomFieldValuesError(null);

    try {
      const data = await leadsService.getLeadCustomFieldValues({ leadId });
      const valuesArr = Array.isArray(data) ? data : [];

      const valuesByField = {};
      valuesArr.forEach((item) => {
        valuesByField[item.custom_field] = { ...item };
      });

      setLeadCustomFieldValues(valuesByField);
    } catch (error) {
      console.error("Error fetching lead custom field values:", error);
      setLeadCustomFieldValues({});
      setLeadCustomFieldValuesError(error);
    } finally {
      setLeadCustomFieldValuesLoading(false);
    }
  }, []);

  const loadCompanies = useCallback(async () => {
    setCompaniesLoading(true);
    setCompaniesError(null);

    try {
      const data = await companiesService.list();
      setCompanies(Array.isArray(data?.results) ? data.results : []);
    } catch (error) {
      console.error("Error fetching companies:", error?.response?.data || error);
      setCompanies([]);
      setCompaniesError(error);
    } finally {
      setCompaniesLoading(false);
    }
  }, []);

  const fetchCustomFieldsByAccount = useCallback(async (accountId) => {
    if (!accountId) {
      setCustomFields([]);
      return;
    }

    setCustomFieldsLoading(true);
    setCustomFieldsError(null);

    try {
      const data = await customFieldsService.fetchCustomFields(accountId);
      setCustomFields(Array.isArray(data) ? data : data?.results || []);
    } catch (error) {
      console.error("Error fetching custom fields:", error);
      setCustomFields([]);
      setCustomFieldsError(error);
    } finally {
      setCustomFieldsLoading(false);
    }
  }, []);

  const loadResponsible = useCallback(async () => {
    try {
      const res = await usersService.mailCorp();
      setResponsible(Array.isArray(res) ? res : []);
    } catch (e) {
      console.error("Error al obtener responsables:", e?.response?.data || e);
    }
  }, []);

  useEffect(() => {
    let mounted = true;

    async function load() {
      try {
        const res = await usersService.mailCorp();
        if (mounted) setResponsible(Array.isArray(res) ? res : []);
      } catch (e) {
        console.error("Error al obtener responsables:", e?.response?.data || e);
      }
    }

    if (!loading && isAuthenticated) {
      load();
    } else if (!isAuthenticated) {
      setResponsible([]);
    }
    return () => {
      mounted = false;
    };
  }, [isAuthenticated, loading]);

  useEffect(() => {
    let mounted = true;

    async function load() {
      try {
        const res = await genericService.types();
        if (mounted) setTypes(Array.isArray(res) ? res : []);
      } catch (e) {
        console.error("Error al obtener tipos:", e?.response?.data || e);
      }
    }

    load();
    return () => {
      mounted = false;
    };
  }, []);

  useEffect(() => {
    let mounted = true;

    async function load() {
      try {
        const all = await usersService.list();
        if (!mounted) return;
        setUsuarios(all);

        const found = all.find((u) => u.username === currentUsername);
        if (found) setCurrentUser(found);
      } catch (e) {
        console.error("Error al obtener usuarios:", e?.response?.data || e);
      }
    }

    if (currentUsername) load();
    return () => {
      mounted = false;
    };
  }, [currentUsername]);

  useEffect(() => {
    let mounted = true;

    async function load() {
      try {
        const res = await genericService.statuses();
        if (mounted) setStatuses(res || []);
      } catch (e) {
        console.error("Error al obtener estados:", e?.response?.data || e);
      }
    }

    load();
    return () => {
      mounted = false;
    };
  }, []);

  useEffect(() => {
    let mounted = true;

    async function load() {
      try {
        const res = await genericService.countries();
        if (mounted) setCountries(res || []);
      } catch (e) {
        console.error("Error al obtener países:", e?.response?.data || e);
      }
    }

    load();
    return () => {
      mounted = false;
    };
  }, []);

  const value = useMemo(
    () => ({
      statuses,
      setStatuses,

      supervisedAccounts,
      accounts,

      usuarios,
      setUsuarios,

      companies,
      setCompanies,
      companiesLoading,
      companiesError,
      loadCompanies,

      types,
      setTypes,

      currentUser,
      countries,
      setCountries,

      responsible,
      setResponsible,
      loadResponsible,

      customFields,
      setCustomFields,
      customFieldsLoading,
      setCustomFieldsLoading,
      customFieldsError,
      setCustomFieldsError,
      fetchCustomFieldsByAccount,

      leadCustomFieldValues,
      setLeadCustomFieldValues,
      leadCustomFieldValuesLoading,
      leadCustomFieldValuesError,
      fetchLeadCustomFieldValues,

      companyDetail,
      setCompanyDetail,
      companyDetailLoading,
      companyDetailError,
      fetchCompanyDetail,

      personsByCompany,
      setPersonsByCompany,
      personsByCompanyLoading,
      personsByCompanyError,
      fetchPersonsByCompany,

      communicationSubjects,
      setCommunicationSubjects,
      communicationSubjectsLoading,
      communicationSubjectsError,
      fetchCommunicationSubjects,
      leadUnreadStatus,

      selectedCommunicationSubjectId,
      setSelectedCommunicationSubjectId,

      conversationMessages,
      setConversationMessages,
      conversationParticipants,
      setConversationParticipants,
      conversationLoading,
      conversationError,
      fetchConversationMessages,
      conversationReminders,
      applyConversationReminders,
      leadReminderStatus,

      selectedCommunicationSubject,
      setSelectedCommunicationSubject,
    }),
    [
      statuses,
      supervisedAccounts,
      accounts,
      usuarios,
      types,
      currentUser,
      countries,
      responsible,
      loadResponsible,
      customFields,
      customFieldsLoading,
      customFieldsError,
      fetchCustomFieldsByAccount,
      companies,
      companiesLoading,
      companiesError,
      loadCompanies,

      leadCustomFieldValues,
      setLeadCustomFieldValues,
      leadCustomFieldValuesLoading,
      leadCustomFieldValuesError,
      fetchLeadCustomFieldValues,

      companyDetail,
      companyDetailLoading,
      companyDetailError,
      fetchCompanyDetail,

      personsByCompany,
      personsByCompanyLoading,
      personsByCompanyError,
      fetchPersonsByCompany,

      communicationSubjects,
      communicationSubjectsLoading,
      communicationSubjectsError,
      fetchCommunicationSubjects,
      leadUnreadStatus,
      selectedCommunicationSubjectId,

      conversationMessages,
      conversationParticipants,
      conversationLoading,
      conversationError,
      fetchConversationMessages,
      conversationReminders,
      applyConversationReminders,
      leadReminderStatus,

      selectedCommunicationSubject,
      setSelectedCommunicationSubject,
    ],
  );

  return <LeadContext.Provider value={value}>{children}</LeadContext.Provider>;
};

export const useLeadContext = () => useContext(LeadContext);
